import {CommentModel} from "./commentModel";
import {AnswerType, CommentType} from "../../definitions/types";
import {getTimeAgo, catalogName} from "../../utils/helpers";

export class AnswerModel {
    private readonly _comments: CommentModel[];
    private readonly _created: Date;
    private readonly _id: number;
    private readonly _internal: boolean;
    private readonly _name: string;
    private readonly _questionId: number;
    private readonly _text: string;
    private readonly _updated?: Date;

    constructor(answerType: AnswerType) {
        this._comments = answerType.comments
            .filter((comment): comment is CommentType => !!(comment !== null && comment.text && comment.name))
            .map(comment => new CommentModel(comment));
        this._created = answerType.created;
        this._id = answerType.id;
        this._internal = answerType.internal;
        this._name = answerType.name!;
        this._questionId = answerType.questionId;
        this._text = answerType.text!;
        this._updated = answerType.updated;
    }

    /**
     * @method comments
     * @description Gets the comments for the answer.
     * @returns {CommentModel[]}
     */
    public get comments(): CommentModel[] {
        return this._comments;
    }

    /**
     * @method commentCount
     * @description Gets the number of comments for the answer.
     * @returns {number}
     */
    public get commentCount(): number {
        return this._comments.length;
    }

    /**
     * @method hasComments
     * @description Gets the flag indicating if the answer has comments.
     * @returns {boolean}
     */
    public get hasComments(): boolean {
        return this.commentCount > 0;
    }

    /**
     * @method commentButtonText
     * @description Gets the text for the comment button.
     * @returns {string}
     */
    public get commentButtonText(): string {
        if (this.commentCount === 0) return 'Leave a comment';
        if (this.commentCount === 1) return '1 Comment';
        return `${this.commentCount} Comments`;
    }

    /**
     * @method created
     * @description Gets the created date for the answer.
     * @returns {Date}
     */
    public get created(): Date {
        return this._created;
    }

    /**
     * @method createdOn
     * @description Gets the created date for the answer in a readable format.
     * @returns {string}
     */
    public get createdOn(): string {
        return this._created.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }

    /**
     * @method createdAgo
     * @description Gets the created date for the answer in a readable format.
     * @returns {string}
     */
    public get createdAgo(): string {
        return getTimeAgo(this._created);
    }

    /**
     * @method byline
     * @description Gets the byline for the question.
     * @returns {string}
     */
    public get byline(): string {
        return `${this.createdAgo} by ${this._internal ? catalogName : this._name}`;
    }

    /**
     * @method id
     * @description Gets the ID for the answer.
     * @returns {number}
     */
    public get id(): number {
        return this._id;
    }

    /**
     * @method internal
     * @description Gets the internal flag for the answer.
     * @returns {boolean}
     */
    public get internal(): boolean {
        return this._internal;
    }

    /**
     * @method name
     * @description Gets the name for the answer.
     * @returns {string}
     */
    public get name(): string {
        return this._name;
    }

    /**
     * @method questionId
     * @description Gets the question ID for the answer.
     * @returns {number}
     */
    public get questionId(): number {
        return this._questionId;
    }

    /**
     * @method text
     * @description Gets the text for the answer.
     * @returns {string}
     */
    public get text(): string {
        return this._text;
    }

    /**
     * @method updated
     * @description Gets the updated date for the answer.
     * @returns {Date | undefined}
     */
    public get updated(): Date | undefined {
        return this._updated;
    }

    /**
     * @method updatedOn
     * @description Gets the updated date for the answer in a human readable format.
     * @returns {string | undefined}
     */
    public get updatedOn(): string | undefined {
        return this._updated
            ? this._updated.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            })
            : undefined;
    }
}